import React, {useCallback, useContext, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ConfirmationContext} from 'src/contexts/confirmation-context';

import {FlatButton} from '@pb/kit-component-button';
import {DropZone} from '@pb/kit-component-dropzone';
import {Group} from '@pb/kit-component-group';


import {setFile, setTemplateData} from '../../../services/add-attendees-bulk.slice';
import {
    BROKEN_TEMPLATE_MESSAGE,
    CHANGED_CHUNK_NAME, INVALID_FILE_TYPE_MESSAGE,
    MATCHING_LIST,
    MAX_FILE_SIZE,
    UPLOAD_BULK_IMPORT_USER_TEMPLATE,
    UPLOAD_FILE_MAX_SIZE_LIMIT_MB,
} from '../constants';
import {
    parseCSVToUserBulkImportType,
    validateBulkImportFile,
} from '../utils/bulkImportFileParser';

import {AddedFile} from './added-files';
import {DropZoneView} from './drop-zone-view';
import {ErrorMessage} from './error-message';

import {
    uploadFileBlockContent,
    uploadFileBlockDropzone,
    uploadFileBlockHeader,
    uploadFileBlockList,
    uploadFileBlockReviewPartition,
    uploadFileBlockUploadPartition,
} from './upload-bulk-import-file.module.styl';

type Props = {
    setUploadFileError: (error: string) => void,
    uploadFileError: string,
    conferenceName: string | undefined,
};

export const UploadBulkUserImportFileBlock = ({uploadFileError, setUploadFileError, conferenceName}: Props) => {
    const dispatch = useDispatch();

    const [addedFile, setAddedFile] = useState<File | null>(null);
    const {addChangedChunk, removeChangedChunk} = useContext(ConfirmationContext);

    const onDrop = useCallback(async (accepted: Array<File>) => {
        let loadedFile = null;
        const file = accepted[0];
        const extension = file.name.toLowerCase().split('.').pop();
        const hasCSVExtension = extension === 'csv';
        const hasCSVType = [
            'text/csv',
            'application/vnd.ms-excel',
        ].includes(file.type);

        if (!(hasCSVExtension || hasCSVType)) {
            setUploadFileError(INVALID_FILE_TYPE_MESSAGE);
        } else if (file.size < 1) {
            setUploadFileError('The uploaded file cannot be empty');
        } else if (file.size > MAX_FILE_SIZE) {
            setUploadFileError(`The maximum file size that can be uploaded is ${UPLOAD_FILE_MAX_SIZE_LIMIT_MB} MB.
            \n Please ensure your file is less than the maximum size`);
        } else {
            try {
                await validateBulkImportFile(file);
                loadedFile = file;
                setUploadFileError('');
            } catch (
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                error: any
            ) {
                setUploadFileError(error?.message || BROKEN_TEMPLATE_MESSAGE);
            }
        }
        setAddedFile(loadedFile);
        if (loadedFile) {
            parseCSVToUserBulkImportType(loadedFile).then(parsedData => {
                addChangedChunk(CHANGED_CHUNK_NAME);
                dispatch(setFile(parsedData.map(record => {
                    const entries = Object.entries(record);
                    return Object.fromEntries(entries.map(([key, value]) => [
                        key,
                        value ? value.toString()?.trim() : '',
                    ]));
                })));
            }, () => {
                setUploadFileError('File can\'t be parsed. Please make sure the file matches the template.');
            });
        }
    }, [setUploadFileError, addChangedChunk, dispatch]);

    const onDownloadTemplateHandler = () => {
        window.open(UPLOAD_BULK_IMPORT_USER_TEMPLATE);
    };

    const addedFilesView = useMemo(() => {
        return (addedFile && !uploadFileError
            ? (
                    <AddedFile
                        addedFile={addedFile}
                        removeFile={() => {
                            dispatch(setTemplateData(null));
                            dispatch(setFile(null));
                            setAddedFile(null);
                            removeChangedChunk(CHANGED_CHUNK_NAME);
                        }}
                    />
                )
            : null
        );
    }, [addedFile, uploadFileError, dispatch, removeChangedChunk]);

    return (
        <Group className={uploadFileBlockContent} direction="v">
            <b>Import User by Uploading List</b>
            <div>
                Upload a list of users to add to the <b>{conferenceName}</b> conference. <b>Please note:</b> Email,
                First name and Last name fields are required.
                Download the <FlatButton onClick={onDownloadTemplateHandler}>Attendees Import template</FlatButton> to
                get started.
                <br/>
                Already existing users will be linked to this conference. Not existing users will be created on
                the <b>Free Newsletter Subscribers account</b> and linked to this conference.
            </div>
            <div className={uploadFileBlockReviewPartition}>
                <Group gap="l" alignItems="start">
                    <div>
                        <div className={uploadFileBlockHeader}><b>Matching Criteria:</b></div>
                        <ul className={uploadFileBlockList}>
                            {MATCHING_LIST.map(({caption, required}, index) => (
                                <li key={caption}>
                                    {`${index + 1}.`}&nbsp;&nbsp;
                                    {required ? <b>{caption}</b> : caption}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={uploadFileBlockUploadPartition}>
                        <DropZone multiple={false} onDrop={onDrop} mix={uploadFileBlockDropzone}>
                            <DropZoneView addedFile={addedFile}/>
                        </DropZone>
                        {addedFilesView}
                        {uploadFileError ? <ErrorMessage message={uploadFileError}/> : null}
                    </div>
                </Group>
            </div>
        </Group>
    );
};
